import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useHeroViewModel } from '../viewmodel/useDemoViewModel';

const HeroView: React.FC = () => {
  const {
    heroContent,
    companyCard,
    jobSeekerCard,
    handleCompanyDemo,
    handleJobSeekerDemo,
    closeDemoModal,
    isDemoModalOpen,
    selectedDemoUrl,
    selectedDemoType,
    isLoading,
    hasError,
    error,
    hasData
  } = useHeroViewModel();

  const modalRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDemoModalOpen) {
        closeDemoModal();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isDemoModalOpen, closeDemoModal]);

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        closeDemoModal();
      }
    };

    if (isDemoModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isDemoModalOpen, closeDemoModal]);

  // Pause video when modal closes
  useEffect(() => {
    if (!isDemoModalOpen && videoRef.current) {
      videoRef.current.pause();
    }
  }, [isDemoModalOpen]);

  // Loading state
  if (isLoading) {
    return (
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            <span className="ml-3 text-white">Loading demos...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
          <div className="text-center text-white">
            <p className="text-lg">Failed to load demos. Please try again.</p>
            <p className="text-sm mt-2 text-blue-200">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2 bg-white text-blue-700 rounded-lg hover:bg-blue-50"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!hasData || !companyCard || !jobSeekerCard) {
    return null;
  }

  return (
    <>
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              {heroContent.title}
              <span className="block text-emerald-400">{heroContent.subtitle}</span>
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl mb-8 sm:mb-12 text-blue-100 max-w-3xl mx-auto px-4">
              {heroContent.description}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
              {/* Companies Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-3">{companyCard.title}</h3>
                  <p className="text-blue-100 mb-6 text-sm sm:text-base">
                    {companyCard.description}
                  </p>
                </div>

                <div className="space-y-3">
                  <Link
                    to={companyCard.buttonLink}
                    className="w-full bg-white text-blue-700 font-semibold py-3 px-4 sm:px-6 rounded-lg hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center text-sm sm:text-base"
                  >
                    {companyCard.buttonText}
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <button
                    className="w-full bg-transparent border-2 border-white/30 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg hover:border-white/50 hover:bg-white/5 transition-all duration-200 text-sm sm:text-base"
                    onClick={handleCompanyDemo}
                  >
                    {companyCard.demoButtonText}
                  </button>
                </div>
              </div>

              {/* Job Seekers Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-3">{jobSeekerCard.title}</h3>
                  <p className="text-blue-100 mb-6 text-sm sm:text-base">
                    {jobSeekerCard.description}
                  </p>
                </div>

                <div className="space-y-3">
                  <Link
                    to={jobSeekerCard.buttonLink}
                    className="w-full bg-emerald-500 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg hover:bg-emerald-600 transition-colors duration-200 flex items-center justify-center text-sm sm:text-base"
                  >
                    {jobSeekerCard.buttonText}
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <button
                    className="w-full bg-transparent border-2 border-white/30 text-white font-semibold py-3 px-4 sm:px-6 rounded-lg hover:border-white/50 hover:bg-white/5 transition-all duration-200 text-sm sm:text-base"
                    onClick={handleJobSeekerDemo}
                  >
                    {jobSeekerCard.demoButtonText}
                  </button>
                </div>

                {jobSeekerCard.badgeText && (
                  <p className="text-xs text-blue-200 mt-4 text-center">
                    {jobSeekerCard.badgeText}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Video Modal */}
      {isDemoModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"></div>

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-1">
            <div
              ref={modalRef}
              className="relative bg-white rounded-2xl shadow-xl mx-auto transform transition-all flex flex-col"
              style={{
                maxWidth: '90vw',
                maxHeight: '90vh'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-2 border-b flex-shrink-0">
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedDemoType === 'company' ? 'Company Demo' : 'Job Seeker Demo'}
                </h3>
                <button
                  onClick={closeDemoModal}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Video Player */}
              <div className="p-2 flex-grow flex items-center justify-center overflow-hidden">
                <div className="bg-black rounded-lg overflow-hidden flex items-center justify-center">
                  {selectedDemoUrl && (
                    <video
                      ref={videoRef}
                      src={selectedDemoUrl}
                      controls
                      autoPlay
                      className="block max-w-full max-h-full w-auto h-auto"
                      controlsList="nodownload"
                      style={{
                        maxWidth: 'calc(90vw - 2rem)',
                        maxHeight: 'calc(90vh - 120px)'
                      }}
                    >
                      <track
                        kind="captions"
                        srcLang="en"
                        label="English captions"
                      />
                      Your browser does not support the video tag.
                    </video>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeroView;